import { type MouseEvent, useContext, useMemo } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { UserContext } from '@contexts/user-context';
import { Alert } from '@pulse-web-ui/alert';
import { FunctionButton } from '@pulse-web-ui/button-v2';
import { observer } from 'mobx-react-lite';

import { MainProducts } from '@entities/index';

import {
  RGS_CAREER,
  STANDARDS,
  TextLink,
  addTestAttribute,
  getSupportMailDataValues,
  useStores,
} from '@shared/index';

import {
  AlertDescription,
  AlertText,
  ContractConclusionStoppedAlertButton,
} from './choose-product.styles';

export const ChooseProduct = observer(() => {
  const { t } = useTranslation();
  const { getUserName } = useContext(UserContext);
  const username = getUserName();
  const navigate = useNavigate();
  const {
    MainStore: {
      agentUserStore: { availableSign, agent },
      agentContractStore: { profile },
    },
  } = useStores();

  const values = useMemo(
    () =>
      getSupportMailDataValues({
        t,
        username,
        profile,
        agent,
      }),
    [t, username, profile, agent]
  );

  const goToContractApplicationPage = (
    event: MouseEvent<HTMLAnchorElement>
  ) => {
    event.preventDefault();
    window.open(RGS_CAREER, '_blank');
  };

  const goToStandartsPage = () => {
    navigate(STANDARDS);
  };

  const shouldConcludeContract = useMemo(
    () =>
      !!availableSign?.visibilityDocuments &&
      !!availableSign.isContractAvailable,
    [availableSign]
  );

  const isUnableToConcludeContract = useMemo(
    () =>
      availableSign?.visibilityDocuments === false &&
      availableSign.isContractAvailable === false,
    [availableSign]
  );

  const ContractConclusionStoppedAlert = () => (
    <Alert
      type='warning'
      title={t('CHOOSE_PRODUCT:alerts.contractConclusionStoppedTitle')}
      description={
        <>
          <AlertDescription>
            {t('CHOOSE_PRODUCT:alerts.contractConclusionStoppedDescription')}
          </AlertDescription>
          <ContractConclusionStoppedAlertButton
            size='s'
            href={RGS_CAREER}
            onClick={goToContractApplicationPage}
            {...addTestAttribute('contract-application-button')}
          >
            {t('CHOOSE_PRODUCT:buttons.leaveRequest')}
          </ContractConclusionStoppedAlertButton>
        </>
      }
    />
  );

  const UnableToConcludeContractAlert = () => (
    <Alert
      type='warning'
      description={
        <AlertText>
          <Trans
            components={{ supportLink: <TextLink href={values.mailtoHref} /> }}
          >
            {t('CHOOSE_PRODUCT:alerts.unableToConcludeContract') +
              t('COMMON:mailto.body')}
          </Trans>
        </AlertText>
      }
    />
  );

  const ReadStandardsAlert = () => (
    <Alert
      type='warning'
      description={
        <>
          <AlertText>{t('CHOOSE_PRODUCT:alerts.standards')}</AlertText>
          <FunctionButton
            size='s'
            onClick={goToStandartsPage}
            {...addTestAttribute('standarts-button')}
          >
            {t('CHOOSE_PRODUCT:buttons.read')}
          </FunctionButton>
        </>
      }
    />
  );

  return (
    <>
      {shouldConcludeContract && <ContractConclusionStoppedAlert />}
      {isUnableToConcludeContract && <UnableToConcludeContractAlert />}
      {availableSign?.visibilityStandard && <ReadStandardsAlert />}
      <MainProducts />
    </>
  );
});
